import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowDownLeft, ArrowUpRight, CreditCard, Bell, X, TrendingUp, Clock, Check, ShoppingBag } from "lucide-react";

import { API } from "@/apiConfig";

export default function HomePage({ profile, onRefresh, onNavigate }) {
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [showAbout, setShowAbout] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, productsRes, purchasesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/wallet/analytics`),
        axios.get(`${API}/products`),
        axios.get(`${API}/products/my-purchases`),
        axios.get(`${API}/app-settings`),
      ]);
      setAnalytics(analyticsRes.data);
      setProducts(productsRes.data);
      setPurchases(purchasesRes.data);
      setAppSettings(settingsRes.data);
    } catch { }
  };

  const activePurchases = purchases.filter(p => !p.is_matured);
  const completedPurchases = purchases.filter(p => p.is_matured);

  return (
    <div className="home-page" data-testid="home-page">
      {/* About Products Popup */}
      {showAbout && (
        <div className="about-popup-overlay" onClick={() => setShowAbout(false)}>
          <div className="about-popup animate-fade-in-up" onClick={e => e.stopPropagation()} data-testid="about-popup">
            <button className="about-popup-close" onClick={() => setShowAbout(false)} data-testid="about-popup-close">
              <X size={18} />
            </button>
            <h3 className="heading-serif text-xl text-white mb-4">About Our Products</h3>
            <div className="about-popup-content">
              {appSettings.about_products_content || "Information about our products will be available soon. Stay tuned for exciting investment opportunities!"}
            </div>
          </div>
        </div>
      )}

      {/* User Banner */}
      <div className="user-banner" data-testid="user-banner">
        <div className="user-banner-left">
          <div className="user-avatar-circle">
            <span>{profile?.masked_phone?.slice(0, 2) || "AP"}</span>
          </div>
          <div>
            <p className="user-id-text">ID:{profile?.user_id || "------"}</p>
            <p className="user-phone-text">Hi, {profile?.masked_phone || "------"}</p>
          </div>
        </div>
        <button className="notification-btn" data-testid="notification-btn">
          <Bell size={20} />
        </button>
      </div>

      {/* Quick Actions + Balance Card */}
      <div className="home-cards-row" data-testid="home-cards">
        <div className="quick-actions-col">
          <button className="quick-action-btn" onClick={() => onNavigate && onNavigate("wallet", "deposit")} data-testid="home-deposit-btn">
            <ArrowDownLeft size={18} />
            <span>Deposit</span>
          </button>
          <button className="quick-action-btn" onClick={() => onNavigate && onNavigate("wallet", "withdraw")} data-testid="home-withdraw-btn">
            <ArrowUpRight size={18} />
            <span>Withdraw</span>
          </button>
          <button className="quick-action-btn" onClick={() => onNavigate && onNavigate("bankcard")} data-testid="home-bankcard-btn">
            <CreditCard size={18} />
            <span>Bank Card</span>
          </button>
        </div>

        <div className="balance-card" data-testid="balance-card">
          <p className="balance-label">Total Balance</p>
          <p className="balance-amount">
            <span className="currency-symbol">&#8377;</span>
            {(profile?.balance || 0).toFixed(0)}
          </p>
          <div className="balance-details">
            <p>Recharge Money <span className="text-white/80">&#8377;{analytics?.recharge_money?.toFixed(2) || "0.00"}</span></p>
            <p>Order <span className="text-white/80">{analytics?.orders || 0}</span></p>
          </div>
        </div>
      </div>

      {/* Transaction Preview */}
      <div className="transaction-preview" data-testid="transaction-preview">
        <div className="user-avatar-small">
          <span>{profile?.masked_phone?.slice(0, 2) || "AP"}</span>
        </div>
        <span className="transaction-text">{profile?.masked_phone || "------"} Recent Activity</span>
      </div>

      {/* Feature Cards - About Products + Task Hall */}
      <div className="feature-cards-row" data-testid="feature-cards">
        <div className="feature-card feature-card-rewards" onClick={() => setShowAbout(true)} style={{cursor: "pointer"}}>
          <h3>About Products</h3>
          <p className="text-xs text-slate-400 mt-1">Learn about our products</p>
          <button className="feature-card-btn" data-testid="about-products-btn">
            <ShoppingBag size={14} />
          </button>
        </div>
        <div className="feature-card feature-card-tasks" onClick={() => onNavigate && onNavigate("product")} style={{cursor: "pointer"}}>
          <h3>Task Hall</h3>
          <p className="text-xs text-slate-400 mt-1">
            {activePurchases.length > 0 ? `${activePurchases.length} active, ${completedPurchases.length} done` : "View your tasks"}
          </p>
          <button className="feature-card-btn" data-testid="task-btn">
            <TrendingUp size={14} />
          </button>
        </div>
      </div>

      {/* Top Picks - Shows Products */}
      <div className="top-picks-section" data-testid="top-picks">
        <div className="flex justify-between items-center mb-3">
          <h2 className="heading-serif text-lg text-white">Top Picks</h2>
          <span className="text-xs text-[#d4af37] cursor-pointer" onClick={() => onNavigate && onNavigate("product")}>More &gt;</span>
        </div>
        <div className="top-picks-scroll">
          {products.length > 0 ? products.map((product, i) => (
            <div key={i} className="top-pick-card" style={{cursor: "pointer"}} onClick={() => onNavigate && onNavigate("product")} data-testid={`top-pick-${i}`}>
              <div className="top-pick-image">
                <span className="top-pick-brand">{product.name}</span>
              </div>
              <p className="top-pick-price">&#8377;{product.price}</p>
              <p className="top-pick-label">{product.maturity_days} Days | &#8377;{product.daily_earning}/day</p>
            </div>
          )) : (
            <>
              <div className="top-pick-card">
                <div className="top-pick-image"><span className="top-pick-brand">AMAZ WEALTH</span></div>
                <p className="top-pick-price">Coming Soon</p>
                <p className="top-pick-label">New Products</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
